/**
 * The publication screen's routes, discovered by `../routes.ts`'s glob.
 * `requires` is the same read capability as every campaign screen: the route
 * refuses a typed URL as well as hiding a link.
 *
 * Its campaign-nav entry nests under Publicações and is always shown: it links
 * to the open post, or to `PUBLICATION_SELECT_PATH`'s picker while none is open.
 */

import { FileText } from 'lucide-react';
import { createElement, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';

import { EntityPage, type EntityOption } from '../../../chrome/entity/EntitySwitcher';
import type { RouteBundle } from '../../routes/contract';
import { useCampaignOrganisationId, useCan } from '../../session/orgContext';
import { usePost, usePosts } from '../api/queries';
import type { PostRow } from '../api/schemas';
import { PUBLICATIONS_PATH } from '../publications/paths';
import { KIND_ICONS } from '../publications/cells';
import { isStory } from '../publications/readings';
import { splitCaption } from './format';
import { pub } from './messages';
import { PUBLICATION_PATH, PUBLICATION_SELECT_PATH } from './paths';
import { Publication } from './Publication';
import { usePublicationWindow } from './search';
import { storyTitle } from './StoryCard';

const CONTROL_PLANE_READ = 'control_plane.read';
const CONTROL_PLANE_WRITE = 'control_plane.write';

function postOption(post: PostRow): EntityOption {
  const cover = post.media[0];
  const kindIcon = KIND_ICONS[post.kind.toUpperCase()];
  return {
    id: post.post_id,
    name: isStory(post)
      ? storyTitle(post)
      : (splitCaption(post.caption).heading ?? pub('post.untitled')),
    detail: post.handle === null ? post.label : `@${post.handle.replace(/^@/, '')}`,
    imageUrl:
      cover?.poster_url ??
      (cover?.media_kind === 'image' ? cover.source_url : null) ??
      post.avatar_url,
    square: true,
    mark:
      kindIcon === undefined
        ? undefined
        : createElement(kindIcon, { 'aria-hidden': true, className: 'size-3.5' }),
  };
}

function PublicationRoute(): ReactNode {
  const { campaignId = '', postId } = useParams({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const tenantId = useCampaignOrganisationId();
  const canReclassify = useCan(CONTROL_PLANE_WRITE);
  const navigate = useNavigate();
  const window = usePublicationWindow();
  const posts = usePosts(campaignId, tenantId ?? '', { tag: '', window });
  // The open post may sit outside the listed page; `Publication` reads the same key, so this
  // names it without a second request.
  const open = usePost(campaignId, tenantId ?? '', postId ?? '', { window });
  if (tenantId === null) return null;

  const rows = posts.data?.rows ?? [];
  const current = open.data?.post ?? null;
  const listed =
    current !== null && !rows.some((row) => row.post_id === current.post_id)
      ? [current, ...rows]
      : rows;

  return createElement(
    EntityPage,
    {
      scope: `post:${campaignId}`,
      options: posts.isError ? 'failed' : posts.isPending ? 'loading' : listed.map(postOption),
      selectedId: postId ?? null,
      labels: {
        select: pub('select.label'),
        title: pub('select.title'),
        search: pub('select.search'),
        none: pub('select.none'),
      },
      onSelect: (id: string) =>
        void navigate({ to: PUBLICATION_PATH, params: { campaignId, postId: id } }),
      embedded: true,
    },
    postId === undefined
      ? null
      : createElement(Publication, { campaignId, tenantId, postId, canReclassify, window }),
  );
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: PUBLICATION_SELECT_PATH,
      element: createElement(PublicationRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
    {
      path: PUBLICATION_PATH,
      element: createElement(PublicationRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
  ],
  nav: [],
  campaignNav: [
    {
      path: PUBLICATION_SELECT_PATH,
      activeOn: PUBLICATION_PATH,
      label: () => pub('nav.post'),
      requires: CONTROL_PLANE_READ,
      parent: PUBLICATIONS_PATH,
      icon: FileText,
      order: 33,
      kind: 'data',
    },
  ],
};
